import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useLayoutEffect, useState, type RefObject } from 'react';

import { DEFAULT_TABLE_ROW_HEIGHT_PX, VIRTUALIZE_ROW_THRESHOLD } from './constants.js';
import type { DataTableDensity } from './dataTableCell.js';

export type DataTableVirtualizeRows = boolean | 'auto';

export function shouldVirtualizeDataTableRows(options: {
  virtualizeRows?: DataTableVirtualizeRows;
  rowCount: number;
  stackedLayout: boolean;
  hasRowTrWrapper: boolean;
  hasScrollParent: boolean;
}): boolean {
  if (!options.hasScrollParent || options.stackedLayout || options.hasRowTrWrapper) return false;
  const mode = options.virtualizeRows ?? 'auto';
  if (mode === false) return false;
  if (mode === true) return options.rowCount > 0;
  return options.rowCount >= VIRTUALIZE_ROW_THRESHOLD;
}

function rowHeightForDensity(density: DataTableDensity): number {
  if (density === 'compact') return DEFAULT_TABLE_ROW_HEIGHT_PX - 6;
  if (density === 'comfortable') return DEFAULT_TABLE_ROW_HEIGHT_PX + 8;
  return DEFAULT_TABLE_ROW_HEIGHT_PX;
}

/** Row virtualization needs a scroll host with measurable height — else tbody clips to 0. */
export function useDataTableScrollParentReady(
  scrollParentRef: RefObject<HTMLElement | null>,
  watch: boolean,
): boolean {
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    if (!watch) {
      setReady(false);
      return;
    }
    const el = scrollParentRef.current;
    if (!el) {
      setReady(false);
      return;
    }
    const measure = () => setReady(el.clientHeight > 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [scrollParentRef, watch]);

  return ready;
}

/** fitViewport + virtual scroll need PageRouteScaffold viewportLock flex chain. */
export function useDataTableViewportLockChain(
  scrollParentRef: RefObject<HTMLElement | null>,
  watch: boolean,
): boolean {
  const [locked, setLocked] = useState(false);

  useLayoutEffect(() => {
    if (!watch) {
      setLocked(false);
      return;
    }
    const el = scrollParentRef.current;
    if (!el) {
      setLocked(false);
      return;
    }
    setLocked(Boolean(el.closest('[data-wp-viewport-lock-body]')));
  }, [scrollParentRef, watch]);

  return locked;
}

export function useDataTableRowVirtualizer(options: {
  enabled: boolean;
  rowCount: number;
  scrollParentRef: RefObject<HTMLElement | null>;
  density: DataTableDensity;
}) {
  const estimateSize = useCallback(() => rowHeightForDensity(options.density), [options.density]);

  return useVirtualizer({
    count: options.enabled ? options.rowCount : 0,
    getScrollElement: () => options.scrollParentRef.current,
    estimateSize,
    overscan: 8,
  });
}
